import { Component, Element, Host, Prop, h } from '@stencil/core';

import { onFireDREvent } from '../../../actions/actions.offer';
import { AutoCheckState } from '../../../store/store.autocheck';
import { ConsumerMarketConfidenceState, initialState as consumerMarketConfidenceInitialState } from '../../../store/store.consumer-market-confidence';
import { CounterofferState, initialState as counterofferInitialState } from '../../../store/store.counteroffer';
import { OfferState, initialState } from '../../../store/store.offer';
import { OfferDetailsModal } from '../offer-details-modal/offer-details-modal';

@Component({
  tag: 'offer-certificate-valid-dr',
  styleUrl: 'offer-certificate-valid-dr.css',
})
export class OfferCertificateValidDr {
  @Element() el: HTMLElement;

  @Prop() autoCheck: AutoCheckState;

  @Prop() customer = { email: '', firstName: '', lastName: '', phone: '', zip: '' };

  @Prop() dealer = {
    name: '',
    isOfficeClosed: false,
    allocated: [],
    dealerOffers: {},
    buyingCenters: [],
    numAssignableDealers: 0,
  };

  @Prop() offer: OfferState = initialState;

  @Prop() vehicle = {
    accidents: false,
    auctionedRecently: false,
    color: '',
    description: '',
    drivetrain: '',
    engine: '',
    fullName: '',
    hasReplacement: false,
    hasTwoSetsOfKeys: true,
    imageUrl: '',
    offerOptions: [],
    make: '',
    isAuctionedLast45: false,
    isRental: false,
    mileage: 0,
    model: '',
    originalOwner: false,
    rentalVehicle: false,
    replacementName: '',
    replacementText: '',
    replacementTypes: '',
    replacementVehicleYearMax: 2023,
    replacementVehicleYearMin: 2018,
    transmission: '',
    trim: '',
    trimId: 0,
    vin: '',
    year: '',
    yearMakeModel: '',
  };

  @Prop() theme: string = '';

  @Prop() optimizely = {
    icocertenhancementsOptimizely: false,
    isAppleWalletPassEnabled: false,
  };

  @Prop() showVehicleDetailDialog: (e: MouseEvent) => void = () => {};
  @Prop() isAdmin: boolean = false;

  @Prop() acceptOrCounteroffer: CounterofferState = counterofferInitialState;
  @Prop() consumerMarketConfidence: ConsumerMarketConfidenceState = consumerMarketConfidenceInitialState;

  trackClickOfferPrice = () => {
    onFireDREvent(this.offer.offerPriceDrButtonText);
  };

  render() {
    const { autoCheckFullReportLink, hasAccidents, hasPriorRentals, hasSingleOwner, reportDate, historyIcons } = this.autoCheck;
    const { firstName, zip } = this.customer;
    const { name } = this.dealer;
    const {
      accidentQuestionModifiedByDealer,
      amount,
      dealerBoostAmount,
      expirationDate,
      offerId,
      formattedFinalOfferAmount,
      offerPriceDrButtonText,
      potentialTaxSaving,
      showOfferPriceDRButton,
    } = this.offer;
    const { fullName, imageUrl, vin } = this.vehicle;

    const hasDealerBoost = dealerBoostAmount > 0;
    const hasVehicleHistory = autoCheckFullReportLink.length > 1;
    const { consumerMarketConfidence } = this;
    const { shouldDisplayAdditionalInsights } = consumerMarketConfidence;
    const shouldDisplayTaxSavings = !shouldDisplayAdditionalInsights && potentialTaxSaving > 0;
    const { isAppleWalletPassEnabled } = this.optimizely;

    return (
      <Host>
        <certificate-layout>
          {hasDealerBoost && <dealer-boost-box dealerName={name} boost={this.offer.dealerBoostAmount} slot="hero" />}

          <congratulation-certificate-message
            consumer={firstName}
            dealer={name}
            vehicleImageLocation={imageUrl}
            vehicle={fullName}
            whenReviewClicked={this.showVehicleDetailDialog.bind(this)}
            slot="left"
            theme={this.theme}
            offerId={offerId}
            zip={zip}
          />

          {amount > 0 && (
            <offer-price
              buttonText={offerPriceDrButtonText}
              expirationDate={expirationDate}
              hasDealerBoost={hasDealerBoost}
              offerAmount={formattedFinalOfferAmount}
              offerId={offerId}
              offerPriceButtonClick={this.trackClickOfferPrice}
              showOfferPriceButton={showOfferPriceDRButton}
              slot="right"
              zip={zip}
              vehicleDescription={fullName}
              showFinalActionButton={!this.acceptOrCounteroffer.shouldDisplay}
              theme={this.theme}
              isAppleWalletPassEnabled={isAppleWalletPassEnabled}
            />
          )}

          <accept-or-counteroffer slot="main" offerAmount={formattedFinalOfferAmount} {...this.acceptOrCounteroffer} />

          {hasVehicleHistory && (
            <vehicle-history-report
              autoCheckFullReportLink={autoCheckFullReportLink}
              hasDealerEditedQuestion={accidentQuestionModifiedByDealer}
              hasAccidents={hasAccidents}
              hasPriorRentals={hasPriorRentals}
              hasSingleOwner={hasSingleOwner}
              reportGeneratedOnDate={reportDate}
              vin={vin}
              slot="main"
              historyIcons={historyIcons}
            />
          )}

          {shouldDisplayTaxSavings && <tax-savings slot="main" potentialTaxSaving={potentialTaxSaving} />}
          <additional-insights {...consumerMarketConfidence} />
          <slot />

          <OfferDetailsModal shouldShowDealerComments={false} autoCheck={this.autoCheck} customer={this.customer} offer={this.offer} vehicle={this.vehicle} />

          {hasDealerBoost ? <offer-certificate-dealer-boost-disclaimer /> : <offer-certificate-disclaimer />}
        </certificate-layout>
      </Host>
    );
  }
}
